import React, { type FormEventHandler, useEffect } from 'react'
import Rx from 'rx'
import {
  ConsentText,
  TermsAndConditionsLink,
} from '@mobi/account/Areas/Verification/Components/Message'
import { spacing } from '@mobi/component-library/Foundations'
import { CheckboxInput } from '@mobi/component-library/Legacy/Input/CheckboxInput'
import { InputField } from '@mobi/component-library/Legacy/Input/InputField'
import { SelectField } from '@mobi/component-library/Legacy/Select'
import { statesInAustralia } from '@mobi/settings/statesInAustralia'
import {
  ChangeValue,
  ContactDetailsFields,
  ContactDetailsState,
  ValidateField,
  ValidationErrors,
  state$ as contactDetailsState$,
} from '@core/Areas/ContactDetails/driver'
import { ReverificationMessage } from '@core/Areas/Reverification/Components/ReverificationMessage'
import { state$ as reverificationState$ } from '@core/Areas/Reverification/driver'
import { observeImmutable } from '@core/Components/HOCs/observe'
import { CountrySelect } from '../CountrySelect/CountrySelect'
import { CheckboxContainer, HorizontalDivider, SectionTitle } from './AdditionalDetails.styles'

interface AdditionalDetailsFields {
  driversLicenceNumber: string
  driversLicenceState: string
  driversLicenceCardNumber: string
  passportNumber: string
  passportCountry: string
  agreeToVerifyAdditionalDetails: boolean
  validationErrors: ValidationErrors
  isReverificationSuccess: boolean
}

export type AdditionalDetailsProps = Pick<ContactDetailsState, keyof AdditionalDetailsFields> // this is for type safety, when ContactDetailsState changes it forces to update AdditionalDetailsFields

const state$ = Rx.Observable.combineLatest(
  contactDetailsState$,
  reverificationState$,
  (contactDetails, reverfication) => ({ contactDetails, reverfication })
)

export const AdditionalDetails = observeImmutable(state$, ({ record }) => {
  const { requiresReverification } = record.reverfication

  if (requiresReverification) {
    return <AdditionalDetailsComponent {...record.contactDetails.toJS()} />
  }
  return null
})

function validateFields() {
  const validationFields: (keyof ContactDetailsFields)[] = [
    'passportCountry',
    'passportNumber',
    'driversLicenceState',
    'driversLicenceNumber',
    'driversLicenceCardNumber',
  ]
  validationFields.forEach(field => ValidateField({ field }))
}

export const AdditionalDetailsComponent = ({
  driversLicenceNumber,
  driversLicenceState,
  driversLicenceCardNumber,
  passportNumber,
  passportCountry,
  agreeToVerifyAdditionalDetails,
  validationErrors,
  isReverificationSuccess,
}: AdditionalDetailsProps) => {
  const changeDriversLicenceState: FormEventHandler<HTMLSelectElement> = event =>
    ChangeValue({ field: 'driversLicenceState', value: event.currentTarget.value })
  const changeDriversLicenceNumber: FormEventHandler<HTMLInputElement> = event =>
    ChangeValue({ field: 'driversLicenceNumber', value: event.currentTarget.value })
  const changeDriversLicenceCardNumber: FormEventHandler<HTMLInputElement> = event =>
    ChangeValue({ field: 'driversLicenceCardNumber', value: event.currentTarget.value })

  const changePassportCountry: FormEventHandler<HTMLSelectElement> = event =>
    ChangeValue({ field: 'passportCountry', value: event.currentTarget.value })
  const changePassportNumber: FormEventHandler<HTMLInputElement> = event =>
    ChangeValue({ field: 'passportNumber', value: event.currentTarget.value })

  useEffect(() => {
    if (!isReverificationSuccess) {
      ChangeValue({ field: 'agreeToVerifyAdditionalDetails', value: false })
    }
  }, [isReverificationSuccess])

  return (
    <div style={{ marginTop: spacing.md }}>
      <ReverificationMessage hasVerificationFailed={isReverificationSuccess === false} />
      <fieldset>
        <SectionTitle>
          <strong>Driver's licence</strong>
        </SectionTitle>
        <SelectField
          name='State of Issue'
          options={statesInAustralia}
          value={driversLicenceState}
          placeholder='Select state'
          onChange={changeDriversLicenceState}
          onBlur={validateFields}
          errorMessage={validationErrors.driversLicenceState}
        />
        <InputField
          type='text'
          name='Licence Number'
          value={driversLicenceNumber || ''}
          onChange={changeDriversLicenceNumber}
          onBlur={validateFields}
          errorMessage={validationErrors.driversLicenceNumber}
        />
        <InputField
          type='text'
          name='Card Number'
          value={driversLicenceCardNumber || ''}
          onChange={changeDriversLicenceCardNumber}
          onBlur={validateFields}
          errorMessage={validationErrors.driversLicenceCardNumber}
        />
      </fieldset>
      <HorizontalDivider />
      <fieldset>
        <SectionTitle>
          <strong>Passport</strong>
        </SectionTitle>
        <CountrySelect
          name='Country of Issue'
          value={passportCountry}
          onChange={changePassportCountry}
          onBlur={validateFields}
          errorMessage={validationErrors.passportCountry}
        />
        <InputField
          name='Passport Number'
          value={passportNumber || ''}
          onChange={changePassportNumber}
          onBlur={validateFields}
          errorMessage={validationErrors.passportNumber}
        />
      </fieldset>
      <HorizontalDivider />

      <AgreeToVerifyAdditionalDetails
        agreeToVerifyAdditionalDetails={agreeToVerifyAdditionalDetails ?? false}
      />
    </div>
  )
}

const AgreeToVerifyAdditionalDetails = ({
  agreeToVerifyAdditionalDetails,
}: {
  agreeToVerifyAdditionalDetails: boolean
}) => {
  const changeAgreeToVerifyAdditionalDetails = (event: React.FormEvent<HTMLInputElement>) =>
    ChangeValue({ field: 'agreeToVerifyAdditionalDetails', value: event.currentTarget.checked })

  return (
    <CheckboxContainer>
      <CheckboxInput
        id='agree-to-verify-checkbox'
        checked={agreeToVerifyAdditionalDetails}
        onChange={changeAgreeToVerifyAdditionalDetails}
        label={
          <>
            <ConsentText /> <br /> <br /> {TermsAndConditionsLink}
          </>
        }
      />
    </CheckboxContainer>
  )
}
